/**
 * Validation Utilities
 * Reusable validation helpers for data validation
 * Compatible with Web and React Native
 */
/**
 * Email validation
 */
export function isEmail(str) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(str);
}
/**
 * Validates email with detailed result
 */
export function validateEmail(str) {
    if (!str) {
        return { valid: false, error: 'Email is required' };
    }
    if (!isEmail(str)) {
        return { valid: false, error: 'Invalid email format' };
    }
    return { valid: true };
}
/**
 * Phone number validation (international format)
 */
export function isPhoneNumber(str) {
    // Matches international phone numbers with optional country code
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;
    const cleaned = str.replace(/\s/g, '');
    return phoneRegex.test(cleaned) && cleaned.length >= 7 && cleaned.length <= 17;
}
/**
 * Validates phone number with detailed result
 */
export function validatePhoneNumber(str) {
    if (!str) {
        return { valid: false, error: 'Phone number is required' };
    }
    if (!isPhoneNumber(str)) {
        return { valid: false, error: 'Invalid phone number format' };
    }
    return { valid: true };
}
/**
 * UUID validation (v4)
 */
export function isUUID(str) {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
}
/**
 * UUID v4 validation specifically
 */
export function isUUIDv4(str) {
    const uuidV4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidV4Regex.test(str);
}
/**
 * URL validation
 */
export function isUrl(str) {
    try {
        new URL(str);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Validates URL with detailed result
 */
export function validateUrl(str, options) {
    if (!str) {
        return { valid: false, error: 'URL is required' };
    }
    if (!isUrl(str)) {
        return { valid: false, error: 'Invalid URL format' };
    }
    if (options?.requireHttps && !str.startsWith('https://')) {
        return { valid: false, error: 'URL must use HTTPS' };
    }
    return { valid: true };
}
/**
 * Minimum string length validation
 */
export function minLength(str, len) {
    return str.length >= len;
}
/**
 * Maximum string length validation
 */
export function maxLength(str, len) {
    return str.length <= len;
}
/**
 * Validates string length within a range
 */
export function validateLength(str, options) {
    const { min, max, exact } = options;
    if (exact !== undefined && str.length !== exact) {
        return { valid: false, error: `Must be exactly ${exact} characters` };
    }
    if (min !== undefined && str.length < min) {
        return { valid: false, error: `Must be at least ${min} characters` };
    }
    if (max !== undefined && str.length > max) {
        return { valid: false, error: `Must be at most ${max} characters` };
    }
    return { valid: true };
}
/**
 * Strong password validation
 * Requires: 8+ chars, uppercase, lowercase, number, special character
 */
export function isStrongPassword(str) {
    if (str.length < 8)
        return false;
    if (!/[A-Z]/.test(str))
        return false;
    if (!/[a-z]/.test(str))
        return false;
    if (!/[0-9]/.test(str))
        return false;
    if (!/[^A-Za-z0-9]/.test(str))
        return false;
    return true;
}
/**
 * Password strength checker
 */
export function getPasswordStrength(str) {
    let score = 0;
    const suggestions = [];
    if (str.length >= 8)
        score += 1;
    else
        suggestions.push('Use at least 8 characters');
    if (str.length >= 12)
        score += 1;
    if (/[A-Z]/.test(str))
        score += 1;
    else
        suggestions.push('Add uppercase letters');
    if (/[a-z]/.test(str))
        score += 1;
    else
        suggestions.push('Add lowercase letters');
    if (/[0-9]/.test(str))
        score += 1;
    else
        suggestions.push('Add numbers');
    if (/[^A-Za-z0-9]/.test(str))
        score += 1;
    else
        suggestions.push('Add special characters');
    // Check for common patterns
    if (/(.)\1{2,}/.test(str)) {
        score -= 1;
        suggestions.push('Avoid repeated characters');
    }
    if (/^[a-z]+$/i.test(str)) {
        score -= 1;
        suggestions.push('Mix character types');
    }
    let level;
    if (score <= 2)
        level = 'weak';
    else if (score <= 3)
        level = 'fair';
    else if (score <= 5)
        level = 'good';
    else
        level = 'strong';
    return { score: Math.max(0, Math.min(score, 6)), level, suggestions };
}
/**
 * Validates password with detailed result
 */
export function validatePassword(str, options) {
    const { minLength: minLen = 8, requireUppercase = true, requireLowercase = true, requireNumber = true, requireSpecial = true, } = options || {};
    if (str.length < minLen) {
        return { valid: false, error: `Password must be at least ${minLen} characters` };
    }
    if (requireUppercase && !/[A-Z]/.test(str)) {
        return { valid: false, error: 'Password must contain an uppercase letter' };
    }
    if (requireLowercase && !/[a-z]/.test(str)) {
        return { valid: false, error: 'Password must contain a lowercase letter' };
    }
    if (requireNumber && !/[0-9]/.test(str)) {
        return { valid: false, error: 'Password must contain a number' };
    }
    if (requireSpecial && !/[^A-Za-z0-9]/.test(str)) {
        return { valid: false, error: 'Password must contain a special character' };
    }
    return { valid: true };
}
/**
 * Checks if string is valid JSON
 */
export function isJson(str) {
    try {
        JSON.parse(str);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Credit card number validation (Luhn algorithm)
 */
export function isCreditCard(str) {
    const cleaned = str.replace(/\D/g, '');
    if (cleaned.length < 13 || cleaned.length > 19) {
        return false;
    }
    // Luhn algorithm
    let sum = 0;
    let isEven = false;
    for (let i = cleaned.length - 1; i >= 0; i--) {
        let digit = parseInt(cleaned[i], 10);
        if (isEven) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }
        sum += digit;
        isEven = !isEven;
    }
    return sum % 10 === 0;
}
/**
 * Gets credit card type from number
 */
export function getCreditCardType(str) {
    const cleaned = str.replace(/\D/g, '');
    const patterns = {
        visa: /^4/,
        mastercard: /^5[1-5]|^2[2-7]/,
        amex: /^3[47]/,
        discover: /^6(?:011|5)/,
        diners: /^3(?:0[0-5]|[68])/,
        jcb: /^(?:2131|1800|35)/,
    };
    for (const [type, pattern] of Object.entries(patterns)) {
        if (pattern.test(cleaned)) {
            return type;
        }
    }
    return null;
}
/**
 * IP address validation (IPv4)
 */
export function isIPv4(str) {
    const parts = str.split('.');
    if (parts.length !== 4)
        return false;
    return parts.every((part) => {
        const num = parseInt(part, 10);
        return !isNaN(num) && num >= 0 && num <= 255 && String(num) === part;
    });
}
/**
 * IP address validation (IPv6)
 */
export function isIPv6(str) {
    const ipv6Regex = /^(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}$|^::(?:[0-9a-fA-F]{1,4}:){0,6}[0-9a-fA-F]{1,4}$|^(?:[0-9a-fA-F]{1,4}:){1,7}:$|^(?:[0-9a-fA-F]{1,4}:){0,6}::(?:[0-9a-fA-F]{1,4}:){0,5}[0-9a-fA-F]{1,4}$/;
    return ipv6Regex.test(str);
}
/**
 * IP address validation (IPv4 or IPv6)
 */
export function isIP(str) {
    return isIPv4(str) || isIPv6(str);
}
/**
 * Hexadecimal validation
 */
export function isHex(str) {
    return /^[0-9a-fA-F]+$/.test(str);
}
/**
 * Base64 validation
 */
export function isBase64(str) {
    if (!str || str.length % 4 !== 0)
        return false;
    return /^[A-Za-z0-9+/]*={0,2}$/.test(str);
}
/**
 * Alphanumeric validation
 */
export function isAlphanumeric(str) {
    return /^[a-zA-Z0-9]+$/.test(str);
}
/**
 * Alpha only validation
 */
export function isAlpha(str) {
    return /^[a-zA-Z]+$/.test(str);
}
/**
 * Numeric string validation
 */
export function isNumericString(str) {
    return /^[0-9]+$/.test(str);
}
/**
 * Slug validation
 */
export function isSlug(str) {
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(str);
}
/**
 * Date string validation (ISO format)
 */
export function isISODate(str) {
    const date = new Date(str);
    return !isNaN(date.getTime()) && str === date.toISOString();
}
/**
 * Date string validation (YYYY-MM-DD)
 */
export function isDateString(str) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(str))
        return false;
    const date = new Date(str);
    return !isNaN(date.getTime());
}
/**
 * Time string validation (HH:MM or HH:MM:SS)
 */
export function isTimeString(str) {
    return /^([01]?[0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/.test(str);
}
/**
 * Required field validation
 */
export function isRequired(value) {
    if (value === null || value === undefined)
        return false;
    if (typeof value === 'string')
        return value.trim().length > 0;
    if (Array.isArray(value))
        return value.length > 0;
    if (typeof value === 'object')
        return Object.keys(value).length > 0;
    return true;
}
/**
 * Validates required with detailed result
 */
export function validateRequired(value, fieldName = 'Field') {
    if (!isRequired(value)) {
        return { valid: false, error: `${fieldName} is required` };
    }
    return { valid: true };
}
/**
 * Number range validation
 */
export function isInRange(num, min, max) {
    return num >= min && num <= max;
}
/**
 * Validates number range with detailed result
 */
export function validateRange(num, options) {
    const { min, max } = options;
    if (min !== undefined && num < min) {
        return { valid: false, error: `Must be at least ${min}` };
    }
    if (max !== undefined && num > max) {
        return { valid: false, error: `Must be at most ${max}` };
    }
    return { valid: true };
}
/**
 * Pattern/regex validation
 */
export function matchesPattern(str, pattern) {
    return pattern.test(str);
}
/**
 * Postal/ZIP code validation (US)
 */
export function isUSPostalCode(str) {
    return /^\d{5}(-\d{4})?$/.test(str);
}
/**
 * Social Security Number validation (US)
 */
export function isSSN(str) {
    const cleaned = str.replace(/\D/g, '');
    if (cleaned.length !== 9)
        return false;
    // Check for invalid patterns
    if (/^0{3}|^6{3}|^9{3}|000$|0000$/.test(cleaned))
        return false;
    return true;
}
/**
 * Username validation
 */
export function isValidUsername(str) {
    // 3-30 chars, starts with letter, alphanumeric and underscores
    return /^[a-zA-Z][a-zA-Z0-9_]{2,29}$/.test(str);
}
/**
 * Validates username with detailed result
 */
export function validateUsername(str) {
    if (!str) {
        return { valid: false, error: 'Username is required' };
    }
    if (str.length < 3) {
        return { valid: false, error: 'Username must be at least 3 characters' };
    }
    if (str.length > 30) {
        return { valid: false, error: 'Username must be at most 30 characters' };
    }
    if (!/^[a-zA-Z]/.test(str)) {
        return { valid: false, error: 'Username must start with a letter' };
    }
    if (!/^[a-zA-Z0-9_]+$/.test(str)) {
        return { valid: false, error: 'Username can only contain letters, numbers, and underscores' };
    }
    return { valid: true };
}
/**
 * Validates multiple conditions and returns all errors
 */
export function validateAll(validations) {
    const errors = [];
    for (const validate of validations) {
        const result = validate();
        if (!result.valid && result.error) {
            errors.push(result.error);
        }
    }
    return { valid: errors.length === 0, errors };
}
// ==================== ORDER AMOUNT VALIDATION ====================
/**
 * Maximum allowed value for DECIMAL(6,2) columns in the OrderLine table.
 * Applies to: UnitPrice, Amount, BasePrice, VariantAdditionalPrice
 */
export const ORDER_MAX_PRICE = 9999.99;
/**
 * Validates that a price/amount value fits within the database DECIMAL(6,2) column.
 */
export function validateOrderPrice(value, fieldName = 'Price') {
    if (value < 0) {
        return { valid: false, error: `${fieldName} cannot be negative` };
    }
    if (value > ORDER_MAX_PRICE) {
        return {
            valid: false,
            error: `${fieldName} (R ${value.toFixed(2)}) exceeds the maximum allowed value of R ${ORDER_MAX_PRICE.toFixed(2)}`,
        };
    }
    return { valid: true };
}
/**
 * Validates that unit price * quantity won't exceed the DECIMAL(6,2) limit.
 * Returns the computed amount if valid.
 */
export function validateOrderLineAmount(unitPrice, quantity) {
    const amount = unitPrice * quantity;
    if (amount > ORDER_MAX_PRICE) {
        return {
            valid: false,
            error: `Line total R ${amount.toFixed(2)} (${quantity} x R ${unitPrice.toFixed(2)}) exceeds the maximum allowed value of R ${ORDER_MAX_PRICE.toFixed(2)}. Consider splitting into multiple lines.`,
        };
    }
    return { valid: true };
}
/**
 * Validates all price fields on an order line request before sending to the API.
 * Throws an Error if any value is out of range.
 */
export function validateOrderLineRequest(line) {
    const errors = [];
    const priceCheck = validateOrderPrice(line.unitPrice, 'Unit price');
    if (!priceCheck.valid)
        errors.push(priceCheck.error);
    const amountCheck = validateOrderLineAmount(line.unitPrice, line.quantity);
    if (!amountCheck.valid)
        errors.push(amountCheck.error);
    if (line.amount !== undefined && line.amount > ORDER_MAX_PRICE) {
        errors.push(`Amount R ${line.amount.toFixed(2)} exceeds the maximum allowed value of R ${ORDER_MAX_PRICE.toFixed(2)}`);
    }
    if (line.basePrice != null && line.basePrice > ORDER_MAX_PRICE) {
        errors.push(`Base price R ${line.basePrice.toFixed(2)} exceeds the maximum allowed value of R ${ORDER_MAX_PRICE.toFixed(2)}`);
    }
    if (line.variantAdditionalPrice != null &&
        line.variantAdditionalPrice > ORDER_MAX_PRICE) {
        errors.push(`Variant additional price R ${line.variantAdditionalPrice.toFixed(2)} exceeds the maximum allowed value of R ${ORDER_MAX_PRICE.toFixed(2)}`);
    }
    if (errors.length > 0) {
        throw new Error(errors.join('; '));
    }
}
/**
 * Creates a validator function from rules
 */
export function createValidator(rules) {
    return (data) => {
        const errors = {};
        let valid = true;
        for (const field in rules) {
            errors[field] = [];
            const value = data[field];
            for (const rule of rules[field]) {
                const result = rule(value);
                if (!result.valid && result.error) {
                    errors[field].push(result.error);
                    valid = false;
                }
            }
        }
        return { valid, errors };
    };
}
//# sourceMappingURL=validation.js.map